import { FC, useEffect } from 'react';
import {
  Control,
  UseFormRegister,
  useWatch,
  UseFormSetValue,
} from 'react-hook-form';
import { DateTime } from 'luxon';
import {
  DatePickerAssessmentQuestion,
  AssessmentAnswer,
  AssessmentQuestionType,
} from '../../../../../../generated/graphql';
import InputGroup from '../../../../../components/InputGroup';
import QuestionField from './QuestionField';
import {
  getAnswerQuestion,
  getAnswerValue,
} from '../../../../../lib/answerHelpers';

interface DatePickerQuestionProps {
  question: DatePickerAssessmentQuestion;
  answer?: AssessmentAnswer;
  idx: number;
  register: UseFormRegister<any>;
  control: Control<any>;
  setValue: UseFormSetValue<any>;
  fieldPrefix?: string; // Optional prefix for nested forms (e.g., "forms.assessmentId")
}

const DatePickerQuestion: FC<DatePickerQuestionProps> = ({
  question,
  answer,
  idx,
  register,
  control,
  setValue,
  fieldPrefix = '',
}) => {
  const baseName = `answers.${idx}.value`;
  const fieldName = fieldPrefix ? `${fieldPrefix}.${baseName}` : baseName;
  const currentValue = useWatch({ control, name: fieldName });

  // Use saved answer if available, otherwise use defaultToToday if configured
  // Match answer by questionType and question text
  const existingAnswerValue =
    answer &&
    answer.questionType === AssessmentQuestionType.DatePicker &&
    getAnswerQuestion(answer) === question.question
      ? getAnswerValue(answer)
      : null;

  // Determine default value: saved answer > defaultToToday > empty
  const defaultValue =
    existingAnswerValue ??
    (question.defaultToToday ? DateTime.now().toFormat('yyyy-MM-dd') : '');

  // Set form value: prioritize saved answer, then defaultToToday
  useEffect(() => {
    if (existingAnswerValue) {
      // Update if no value set, or if current value is default but we have a saved answer
      const defaultDateValue = question.defaultToToday
        ? DateTime.now().toFormat('yyyy-MM-dd')
        : '';
      if (
        !currentValue ||
        (currentValue === defaultDateValue &&
          existingAnswerValue !== defaultDateValue)
      ) {
        setValue(fieldName, existingAnswerValue);
      }
    } else if (!currentValue && question.defaultToToday) {
      // No saved answer, use defaultToToday if configured
      setValue(fieldName, DateTime.now().toFormat('yyyy-MM-dd'));
    }
  }, [
    existingAnswerValue,
    currentValue,
    setValue,
    fieldName,
    question.defaultToToday,
  ]);

  return (
    <div>
      <QuestionField question={question.question} />
      <InputGroup
        type="date"
        label={question.question}
        {...register(fieldName, {
          required: question.isRequired,
        })}
        defaultValue={defaultValue}
        labelHidden
        inputSize="extra-small"
        containerClassName="w-full md:w-[398px]"
      />
    </div>
  );
};

export default DatePickerQuestion;
